import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { useParams } from "react-router-dom";
import { PageRenderer } from '@/components/pagebuilder/PageRenderer';
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { api } from "@/api";
import { toBackendUrl, resolveApiBase } from "@/lib/utils";

// Öffentliche Landing Page für /scan/:slug
// Minimaler Flow: Nutzer gibt E-Mail + Name ein, optional Newsletter, bekommt Code
const ScanPage = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { customer, isAuthenticated: isCustomerAuthenticated, logout } = useCustomerAuth();
  const [pageData, setPageData] = useState(null); // published pagebuilder page
  const [pageError, setPageError] = useState(null);
  const [pageLoading, setPageLoading] = useState(true); // track if page data is still loading
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [newsletter, setNewsletter] = useState(false);
  const [meta, setMeta] = useState(null);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState(1); // 1=form start, 2=enter token (debug), 3=show code
  const [pendingToken, setPendingToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [redeemQr, setRedeemQr] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close user dropdown on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  useEffect(() => {
    if (!isCustomerAuthenticated || !customer) return;
    const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(" ").trim();
    if (!email && customer.email) setEmail(customer.email);
    if (!name && fullName) setName(fullName);
  }, [isCustomerAuthenticated, customer, email, name]);

  // Track page view
  useEffect(() => {
    if (!slug) return;
    const API = resolveApiBase();
    fetch(`${API}/public/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, source: 'scan' }),
    }).catch(() => {});
  }, [slug]);

  // Lade Action Meta + versuche zugehörige PageBuilder Seite (Slug: act-<qrSlug>)
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const res = await fetch(`${resolveApiBase()}/qr/action/${encodeURIComponent(slug)}`);
        if (!res.ok) return; // still allow scan even if meta fehlt
        const data = await res.json();
        if (!abort) setMeta(data);
      } catch (_) {
        /* ignore */
      }
    })();
    // PageBuilder Seite (published). Prefer action linkage (for-scan), then fallback to direct act-<slug>.
    (async () => {
      try {
        const base = resolveApiBase();
        // 1) Resolve via backend helper tied to the Action linkage
        let r = await fetch(`${base}/pages/for-scan/${encodeURIComponent(slug)}`);
        const j = await r.json();
        if (r.ok && !j?.error) {
          if (!abort) {
            setPageData(j);
            setPageLoading(false);
          }
          return;
        }
        // 2) Fallback: try direct slug act-<slug>
        const pageSlug = 'act-' + slug; // corresponds to initial creation in backend actions route
        r = await fetch(`${base}/pages/slug/${pageSlug}`);
        if (r.ok) {
          const j2 = await r.json();
          if (!abort) {
            setPageData(j2);
            setPageLoading(false);
          }
          return;
        }
        if (!abort) {
          setPageError(j?.error || 'Not found');
          setPageLoading(false);
        }
      } catch(e){ 
        if(!abort) {
          setPageError(e.message);
          setPageLoading(false);
        }
      }
    })();
    return () => {
      abort = true;
    };
  }, [slug]);

  // Theme injection for PageBuilder page on scan route
  useEffect(()=>{
    if(!pageData?.theme) return;
    const styleElId = 'pb-theme';
    let el = document.getElementById(styleElId);
    if(!el){ el = document.createElement('style'); el.id = styleElId; document.head.appendChild(el); }
    const colors = pageData.theme.colors || {};
    const headingFont = pageData.theme.typography?.headingFont || '';
    const bodyFont = pageData.theme.typography?.bodyFont || '';
    const bodyStack = `var(--pb-font-body, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif)`;
    const headingStack = `var(--pb-font-heading, inherit)`;
    el.innerHTML = `:root{--pb-primary:${colors.primary || '#2563eb'};--pb-surface:${colors.surface || '#ffffff'};${bodyFont?`--pb-font-body:'${bodyFont}', system-ui;`:''}${headingFont?`--pb-font-heading:'${headingFont}', ${bodyFont || 'system-ui'};`:''}}
      .pb-root{ font-family: ${bodyStack}; }
      .pb-root h1,.pb-root h2,.pb-root h3,.pb-root h4,.pb-root h5,.pb-root h6{ font-family: ${headingStack}; }`;
    const addFontLink = (family, id)=>{
      if(!family) return;
      if(document.getElementById(id)) return;
      const f = family.replace(/ /g,'+');
      const link = document.createElement('link');
      link.id = id; link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${f}:wght@400;600;700&display=swap`;
      document.head.appendChild(link);
    };
    addFontLink(headingFont, 'pb-font-link-heading');
    addFontLink(bodyFont, 'pb-font-link-body');
  }, [pageData]);

  // Token replacement when rendering pageData
  const replacedTree = useMemo(()=>{
    if(!pageData?.page) return null;
    const action = pageData.action || {};
    // Try to obtain company/campaign names from either pageData or separate meta fetch
    const companyName = (pageData.company && pageData.company.name) || (meta && meta.company && meta.company.name) || '';
    const campaignName = (meta && meta.campaign && meta.campaign.name) || '';
    const company = (pageData.company) || (meta && meta.company) || {};
    const tokens = {
      '{{action.title}}': action.title || '',
      '{{action.discountType}}': action.discountType || '',
      '{{action.discountValue}}': action.discountValue != null ? String(action.discountValue) : '',
      '{{action.companyName}}': companyName,
      '{{action.campaignName}}': campaignName,
      '{{company.addressLine1}}': company.addressLine1 || '',
      '{{company.addressLine2}}': company.addressLine2 || '',
      '{{company.postalCode}}': company.postalCode || '',
      '{{company.city}}': company.city || '',
      '{{company.country}}': company.country || '',
    };
    try {
      const str = JSON.stringify(pageData.page);
      const replaced = Object.keys(tokens).reduce((acc,k)=>acc.split(k).join(tokens[k]), str);
      return JSON.parse(replaced);
    } catch (err) { 
      console.error('[ScanPage] Token replacement failed:', err);
      return pageData.page; 
    }
  }, [pageData, meta]);

  const submitStart = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await api.publicScan({
        slug,
        email,
        name,
        consentNewsletter: newsletter,
      });
      if (r.token) {
        setPendingToken(r.token);
        setStep(2);
      } else {
        // Mail verschickt – keine Token-Eingabe nötig
        setPendingToken("");
        setStep(2); // Step 2 just shows info
      }
    } catch (e) {
      setError(e.message || t('public.scan.error') || t('public.verify.error') || 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await api.publicVerify(pendingToken.trim());
      setResult(r);
      setStep(3);
      try {
        const redeemUrl = `${window.location.origin}/redeem/${r.code}`;
        const dataUrl = await QRCode.toDataURL(redeemUrl, {
          margin: 1,
          scale: 6,
          errorCorrectionLevel: "M",
        });
        setRedeemQr({ dataUrl, redeemUrl });
      } catch (_) {
        setRedeemQr(null);
      }
    } catch (e) {
      setError(e.message || t('public.scan.error') || t('public.verify.error') || 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while page data is being fetched
  if(pageLoading){
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  // Shared navbar across all views
  const loginRedirect = encodeURIComponent(window.location.pathname + window.location.search);
  const customerInitials = customer
    ? ([customer.firstName, customer.lastName].filter(Boolean).map(s => s[0]).join('') || (customer.email || '?')[0]).toUpperCase()
    : '?';
  const customerDisplayName = customer
    ? [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim() || customer.email || ''
    : '';

  const ScanNavbar = () => (
    <nav className="bg-background border-b border-border sticky top-0 z-50 shadow-sm">
      <div className="max-w-2xl mx-auto px-4 flex items-center justify-between" style={{ height: '52px' }}>
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 text-sm font-semibold text-foreground hover:opacity-80 transition-opacity">
          <img src="/favicon.svg" alt="Scanmirein" className="w-7 h-7" />
          <span className="tracking-tight">Scanmirein</span>
        </a>

        <div className="flex items-center gap-3">
          {/* Zur Landingpage als Home-Icon */}
          <a
            href="/"
            title={t('public.header.backToLanding', { defaultValue: 'Zur Landingpage' })}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
              <path d="M9 21V12h6v9" />
            </svg>
          </a>

          {/* User area */}
          {isCustomerAuthenticated ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(o => !o)}
                className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-muted transition-colors"
              >
                {/* Avatar circle with initials */}
                <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center shrink-0 select-none">
                  {customerInitials}
                </span>
                <span className="text-xs font-medium text-foreground max-w-[120px] truncate hidden sm:block">
                  {customerDisplayName}
                </span>
                {/* Chevron */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-1.5 w-44 bg-background border border-border rounded-lg shadow-lg py-1 z-50">
                  <a
                    href="/customer/dashboard"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="3" width="7" height="7" rx="1" />
                      <rect x="3" y="14" width="7" height="7" rx="1" />
                      <rect x="14" y="14" width="7" height="7" rx="1" />
                    </svg>
                    Dashboard
                  </a>
                  <div className="border-t border-border my-1" />
                  <button
                    onClick={() => { setUserMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Abmelden
                  </button>
                </div>
              )}
            </div>
          ) : (
            <a
              href={`/customer/login?redirect=${loginRedirect}`}
              className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-full transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              {t('public.header.login', { defaultValue: 'Anmelden' })}
            </a>
          )}
        </div>
      </div>
    </nav>
  );

  // Wenn PageBuilder Seite existiert -> rendern. PageRenderer zeigt selbst "Empty page" falls root fehlt.
  if(replacedTree){
    return (
      <div className="min-h-screen bg-muted">
        <ScanNavbar />
        <div className="py-10">
          <div className="max-w-2xl mx-auto px-4">
            <PageRenderer tree={replacedTree} meta={{ action: pageData.action || null, company: pageData.company || (meta ? meta.company : null) || null }} />
          </div>
        </div>
      </div>
    );
  }

  // Fallback (legacy Form) falls keine PageBuilder Seite vorhanden
  return (
    <div className="min-h-screen bg-muted">
      <ScanNavbar />
      <div className="flex items-center justify-center px-4 py-12">
      <form
        onSubmit={
          step === 1
            ? submitStart
            : step === 2
            ? submitVerify
            : (e) => e.preventDefault()
        }
        className="bg-background shadow rounded p-6 w-full max-w-md space-y-4"
      >
        {meta && (
          <div className="flex items-center gap-2 pb-3 border-b border-border">
            {meta?.company?.logoUrl && (
              <img src={toBackendUrl(meta.company.logoUrl)} alt="Logo" className="w-7 h-7 rounded-full object-cover shrink-0" />
            )}
            <h1 className="text-sm font-semibold leading-tight truncate">
              {t('public.scan.title', { title: (meta ? meta.title : t('public.scan.action')) })}
            </h1>
          </div>
        )}
        {step === 1 && (
          <>
            <input
              placeholder={t('public.scan.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border px-3 py-2 rounded w-full text-sm"
              required
            />
            <input
              placeholder={t('public.scan.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border px-3 py-2 rounded w-full text-sm"
              required
            />
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={newsletter}
                onChange={(e) => setNewsletter(e.target.checked)}
              />{" "}
              {t('public.scan.newsletterConsent')}
            </label>
          </>
        )}
        {step === 2 && pendingToken && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {t('public.scan.tokenPrompt')}
            </p>
            <input
              placeholder={t('public.scan.tokenPlaceholder')}
              value={pendingToken}
              onChange={(e) => setPendingToken(e.target.value)}
              className="border px-3 py-2 rounded w-full text-sm"
              required
            />
          </div>
        )}
        {step === 2 && !pendingToken && (
          <p className="text-xs text-muted-foreground">
            {t('public.scan.emailSentInfo')}
          </p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {step < 3 && (pendingToken || step === 1) && (
          <button
            disabled={loading}
            className="w-full bg-blue-600 text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {loading
              ? t('public.scan.button.sending')
              : step === 1
              ? t('public.scan.button.confirmEmail')
              : t('public.scan.button.verifyToken')}
          </button>
        )}
        {step === 2 && !pendingToken && (
          <p className="text-center text-xs text-muted-foreground">
            {t('public.scan.openEmailAndClick')}
          </p>
        )}
        {step === 3 && result && (
          <div className="text-xs bg-muted p-3 rounded space-y-2 text-center">
            <div className="break-all">
              <strong>{t('public.scan.resultTitle')}</strong> {result.code}
              {result.discountType && (
                <div>
                  {t('public.scan.discount')}: {result.discountType} {result.discountValue}
                </div>
              )}
            </div>
            {redeemQr && (
              <div className="space-y-1">
                <img
                  src={redeemQr.dataUrl}
                  alt="Redeem QR"
                  className="mx-auto border p-1 bg-background"
                />
                <button
                  type="button"
                  onClick={() =>
                    navigator.clipboard.writeText(redeemQr.redeemUrl)
                  }
                  className="underline text-blue-600"
                >
                  {t('public.scan.copyLink')}
                </button>
              </div>
            )}
          </div>
        )}
      </form>
      </div>
    </div>
  );
};

export default ScanPage;
